import { FieldValues } from 'react-hook-form'
import { DataItem, load } from 'scrivito'

import {
  getDocumentScope,
  getRelationFilter,
} from '@/Components/Document/documentUtils'
import {
  DocumentRelationType,
  FolderStackItem,
} from '@/Components/Document/types'
import { Document } from '@/Data/Document/DocumentDataClass'
import { DocumentDocumentRelation } from '@/Data/DocumentDocumentRelation/DocumentDocumentRelationDataClass'
import {
  uploadDocumentsSequential,
  DocumentsUploadOptions,
} from '@/hooks/documents/uploadDocuments'

interface UseDocumentOperationsProps {
  entityId: string | null
  relationTo: DocumentRelationType | undefined
  folderStack: FolderStackItem[]
  setFolderStack: React.Dispatch<React.SetStateAction<FolderStackItem[]>>
  setError: React.Dispatch<React.SetStateAction<unknown>>
  openFolder: (folderId: string) => void
  loadDocumentsForEntity: () => void
}
/**
 * Hook that provides low-level document operations like upload, create folder,
 * and delete relation. Automatically maintains the folder stack and refresh logic.
 *
 * @internal Do not use this hook directly. Use the DocumentContextProvider and
 * its exported context instead.
 *
 */
export const useDocumentOperations = ({
  entityId,
  relationTo,
  folderStack,
  setFolderStack,
  setError,
  openFolder,
  loadDocumentsForEntity,
}: UseDocumentOperationsProps) => {
  const currentFolderId = folderStack[folderStack.length - 1]?.id

  const refresh = (overrideFolderId?: string) => {
    const targetId = overrideFolderId ?? folderStack[folderStack.length - 1]?.id
    if (targetId) {
      openFolder(targetId)
    } else {
      loadDocumentsForEntity()
    }
  }

  const createRelationFor = async (documentId: string) => {
    if (!entityId || !relationTo) {
      return
    }
    if (currentFolderId) {
      // create doc-doc relation for documents inside folders
      await DocumentDocumentRelation.all().create({
        father: currentFolderId,
        child: documentId,
      })
    } else {
      const scope = getDocumentScope(relationTo)
      const relationKey = getRelationFilter(relationTo)

      await scope?.create({
        document: documentId,
        [relationKey]: entityId,
      })
    }
  }

  const uploadDocuments = async (
    files: File[],
    opts?: DocumentsUploadOptions,
  ) => {
    if (!files || (!relationTo && !opts?.allowNoRelation)) {
      return
    }
    try {
      await uploadDocumentsSequential(files, {
        ...opts,
        afterCreate: async (documentId) => {
          if (relationTo) {
            await createRelationFor(documentId)
          }
          await opts?.afterCreate?.(documentId)
        },
      })
      refresh()
    } catch (error) {
      setError(error)
    }
  }

  const createFolder = async (formData: FieldValues): Promise<void> => {
    try {
      const folder = await Document.create({ ...formData, folder: true })
      if (folder) {
        const folderId = folder.id()
        await createRelationFor(folderId)
        const foldername = (folder?.get('displayTitle') as string) || 'Folder'
        setFolderStack((stack) => {
          const alreadyTop = stack[stack.length - 1]?.id === folderId
          return alreadyTop
            ? stack
            : [...stack, { id: folderId, name: foldername }]
        })
        refresh(folderId)
      }
    } catch (error) {
      setError(error)
    }
  }

  const deleteRelation = async (documentId: string): Promise<void> => {
    load(async () => {
      if (!relationTo) {
        return
      }
      let relation: DataItem | undefined
      try {
        if (currentFolderId) {
          relation = DocumentDocumentRelation.all()
            .transform({
              filters: {
                father: currentFolderId,
                child: documentId,
              },
            })
            .take()[0]
        } else {
          const scope = getDocumentScope(relationTo)
          if (!scope) return
          const filterKey = getRelationFilter(relationTo)
          const dataFilters = {
            [filterKey]: entityId,
            document: documentId,
          }
          relation = scope
            .transform({
              // contact, project, activity
              filters: dataFilters,
            })
            .take()[0]
        }
        await relation?.delete()
        refresh()
      } catch (error) {
        setError(error)
      }
    })
  }
  const deleteFolder = async (folderId: string): Promise<void> => {
    load(async () => {
      try {
        await deleteRelation(folderId).then(async () => {
          refresh()
        })
      } catch (error) {
        setError(error)
      }
    })
  }

  return {
    createFolder,
    deleteRelation,
    deleteFolder,
    uploadDocuments,
    refresh,
  }
}
